import { RecommendationResponse } from 'types/billing';
import { QaBillingState } from './quotaFixtures';

// Keyed by qa_billing_state (same keys as QUOTA_FIXTURES) so one param drives
// all mock sources. Only active trials with accrued usage are available (PLT-4059).
export const RECOMMENDATION_FIXTURES: Record<
  QaBillingState,
  RecommendationResponse
> = {
  // Day 1, no usage yet → "we'll recommend once we see your usage" hero.
  'trial-first-day': {
    available: false,
    basedOnDays: 1,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  'trial-early': {
    available: true,
    basedOnDays: 3,
    monthlyCredits: 4_800,
    recommendedPlan: { credits: 5_000, monthlyCostUsd: 60 },
    enterpriseHandoff: false,
  },

  'trial-ending': {
    available: true,
    basedOnDays: 13,
    monthlyCredits: 17_500,
    recommendedPlan: { credits: 20_000, monthlyCostUsd: 240 },
    enterpriseHandoff: false,
  },

  'trial-last-day': {
    available: true,
    basedOnDays: 14,
    monthlyCredits: 17_500,
    recommendedPlan: { credits: 20_000, monthlyCostUsd: 240 },
    enterpriseHandoff: false,
  },

  // Paid lifecycle states have no trial recommendation.
  healthy: {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  approaching: {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  overage: {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  blocked: {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  'blocked-trial-end': {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  // Lapsed trial → no recommendation (trial is over).
  'trial-lapsed': {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  'blocked-payment-failed': {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  'blocked-subscription-canceled': {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },

  // Migrated free user — day 1, no usage yet. BE always returns available:true
  // and the smallest SKU (2500/$30) as the floor for zero-usage free accounts.
  'trial-migrated-free': {
    available: true,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: { credits: 2_500, monthlyCostUsd: 30 },
    enterpriseHandoff: false,
  },

  // Migrated free user who had an annual prepaid plan — shows refund note.
  // Same recommendation floor as trial-migrated-free.
  'trial-migrated-free-annual': {
    available: true,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: { credits: 2_500, monthlyCostUsd: 30 },
    enterpriseHandoff: false,
  },

  // Migrated free user whose trial has lapsed — recommendation no longer applies.
  'trial-migrated-free-lapsed': {
    available: false,
    basedOnDays: 0,
    monthlyCredits: 0,
    recommendedPlan: null,
    enterpriseHandoff: false,
  },
} as const;
